import { useEffect, useState } from 'react';
import type { StepProps } from '../../OBWWizard';
import { fetchPrefillToken, PowApiError, type PrefillTokenResponse } from '../../../lib/pow-api';

// Phase 5c.5 (ADR-030 D3 Step 4) — fetch the pre-fill token on mount, populate
// read-only (CN + email) and editable (businessName + websiteUrl) fields,
// pass the token forward through step data for PartnerSubmitStep.
//
// The pre-fill token is NOT in URL query params (cleared in 5c.5 — see
// PartnersApplyPage where the standalone form lived). It comes from
// GET /portal/pow/prefill-token. Token has 15-min TTL per ADR-027 §4.1; full
// server-side validation happens at POST /portal/pow/submit.

/**
 * POW-C — normalise a user-entered website to the canonical `https://host` form,
 * or null if it is not a plausible domain. Accepts bare (`yourbusiness.com`),
 * `www.`-prefixed, and full-URL inputs. Mirrors the shared domain-verify
 * `normaliseDomainInput` (StepDomainVerify) so the value forwarded to
 * POST /portal/pow/submit already passes the server-side validateApplicantUrl
 * (which requires https + rejects path/query/userinfo).
 */
function normalisePartnerWebsite(input: string): string | null {
  let raw = input.trim().toLowerCase();
  if (!raw) return null;
  if (!/^https?:\/\//i.test(raw)) raw = `https://${raw}`;
  let host: string;
  try {
    host = new URL(raw).hostname;
  } catch {
    return null;
  }
  const bare = host.replace(/^www\./, '');
  // At least one dot + valid DNS labels — rejects "localhost", trailing-dot, bad chars.
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(bare)) {
    return null;
  }
  return `https://${bare}`;
}

export function PartnerPrefillConfirmStep({ data, onNext }: StepProps) {
  // If we've already advanced and come back, prefer the stashed data.
  const stashedToken = data?.preFillToken as string | undefined;
  const stashedCn = data?.customerNumber as string | undefined;
  const stashedEmail = data?.email as string | undefined;
  const stashedBusinessName = (data?.businessName as string | undefined) ?? '';
  const stashedWebsite = (data?.websiteUrl as string | undefined) ?? '';

  const [prefill, setPrefill] = useState<PrefillTokenResponse | null>(
    stashedToken && stashedCn && stashedEmail
      ? {
          token: stashedToken,
          issuedAt: 0,
          expiresAt: 0,
          ttlSeconds: 0,
          prefill: {
            customerNumber: stashedCn,
            email: stashedEmail,
            businessName: stashedBusinessName,
            websiteUrl: stashedWebsite,
          },
        }
      : null,
  );
  const [loading, setLoading] = useState(!prefill);
  const [error, setError] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState(stashedBusinessName);
  const [websiteUrl, setWebsiteUrl] = useState(stashedWebsite);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    if (prefill) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchPrefillToken();
        if (cancelled) return;
        setPrefill(res);
        if (!businessName) setBusinessName(res.prefill.businessName || '');
        if (!websiteUrl) setWebsiteUrl(res.prefill.websiteUrl || '');
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof PowApiError && err.code === 'CUSTOMER_NUMBER_UNAVAILABLE') {
          setError('A customer number has not been provisioned for your account yet. Please contact support.');
        } else {
          setError('Could not load the application form. Please try again in a moment.');
        }
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [prefill, businessName, websiteUrl]);

  function commit() {
    if (!prefill) return;
    // POW-C — normalise bare domains ("yourbusiness.com" / "www.foo.com") to the
    // canonical https://host the server-side validateApplicantUrl expects, so a
    // subscriber typing a bare domain submits cleanly instead of failing the
    // https-scheme check. Mirrors the shared domain-verify normaliser.
    const norm = normalisePartnerWebsite(websiteUrl);
    if (!norm) { setUrlError('That doesn\'t look like a valid website domain.'); return; }
    if (!businessName.trim()) { setUrlError(null); return; }
    setWebsiteUrl(norm);
    onNext({
      preFillToken: prefill.token,
      customerNumber: prefill.prefill.customerNumber,
      email: prefill.prefill.email,
      businessName: businessName.trim(),
      websiteUrl: norm,
    });
  }

  if (loading) {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Confirm your details</h2>
        <div className="obw-ai-status" aria-busy="true" aria-live="polite">
          <span className="obw-ai-spinner" aria-hidden="true" />
          <span>Loading your subscriber details…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="obw-step-content">
        <h2 className="obw-title">Confirm your details</h2>
        <div className="portal-wizard-error" role="alert">{error}</div>
        <p className="obw-subtitle" style={{ marginTop: '16px' }}>
          <a href="/" style={{ color: 'var(--accent)' }}>← Back to dashboard</a>
        </p>
      </div>
    );
  }

  if (!prefill) return null;

  return (
    <div className="obw-step-content">
      <h2 className="obw-title">Confirm your details</h2>
      <p className="obw-subtitle">
        We've pre-filled the details from your subscriber account. Confirm or correct the
        business-facing fields. Customer number and email are tied to your account and can't be
        changed here.
      </p>

      <p className="obw-form-section">Read-only — from your subscriber account</p>
      <div className="obw-form-grid-2">
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="pa-cn">Customer number</label>
          <input
            id="pa-cn"
            className="obw-form-input"
            type="text"
            value={prefill.prefill.customerNumber}
            readOnly
            aria-readonly="true"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-dim)' }}
          />
        </div>
        <div className="obw-form-group">
          <label className="obw-form-label" htmlFor="pa-email">Email</label>
          <input
            id="pa-email"
            className="obw-form-input"
            type="email"
            value={prefill.prefill.email}
            readOnly
            aria-readonly="true"
            style={{ background: 'var(--bg-elevated)', color: 'var(--text-dim)' }}
          />
        </div>
      </div>

      <p className="obw-form-section">Confirm — these go on your partner application</p>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-businessName">Business name *</label>
        <input
          id="pa-businessName"
          className="obw-form-input"
          type="text"
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          maxLength={200}
          required
        />
      </div>
      <div className="obw-form-group">
        <label className="obw-form-label" htmlFor="pa-websiteUrl">Primary website *</label>
        <input
          id="pa-websiteUrl"
          className="obw-form-input"
          type="text"
          inputMode="url"
          value={websiteUrl}
          onChange={(e) => { setWebsiteUrl(e.target.value); setUrlError(null); }}
          onBlur={() => {
            if (!websiteUrl.trim()) return;
            const norm = normalisePartnerWebsite(websiteUrl);
            if (norm) { setWebsiteUrl(norm); setUrlError(null); }
            else setUrlError('That doesn\'t look like a valid website domain.');
          }}
          placeholder="yourbusiness.com"
          required
        />
        {urlError && <p className="obw-form-hint" style={{ color: 'var(--danger)' }}>{urlError}</p>}
      </div>

      <div className="portal-wizard-nav">
        <button
          type="button"
          className="obw-btn-primary"
          onClick={commit}
          disabled={!businessName.trim() || !websiteUrl.trim()}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
